import React from "react";
import {autobind} from "core-decorators";
import {action, observable} from "mobx";
import {inject, observer} from "mobx-react";
import {Button, Modal, Popover, Select, Tooltip} from "antd";
import {
  AudioOutlined,
  PlusOutlined,
  QuestionOutlined,
  ReadOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {Link, withRouter, RouteComponentProps} from "react-router-dom";
import _castArray from "lodash/castArray";
import _isEmpty from "lodash/isEmpty";
import _isString from "lodash/isString";

import "./Header.scss";
import {paths} from "@/routes";
import logo from "./logo.svg";
import Store from "@/stores";
import AccountPopover from "./AccountPopover";
import AddPieceUpgradeModal from "@/components/AddPieceUpgradeModal/AddPieceUpgradeModal";

interface IHeaderProps extends RouteComponentProps {
  children?: React.ReactNode;
  store?: Store;
}

const {Option, OptGroup} = Select;

@inject("store")
@autobind
@observer
class Header extends React.Component<IHeaderProps, any> {
  @observable addPieceModalVisible = false;
  @observable isAccountClicked = false;
  @observable isAccountHovered = false;

  buttonActiveProps(path: string | string[]) {
    const {pathname} = this.props.location;
    const paths = _castArray(path);
    return paths.some((p) => p === pathname) ? ({type: "primary"} as const) : {};
  }

  @action
  closeAddPieceModal() {
    this.addPieceModalVisible = false;
  }

  @action
  handleAccountClickChange(open: boolean) {
    this.isAccountClicked = open;
    // Hide tooltip when the popover is open
    this.isAccountHovered = false;
  }

  @action
  handleAccountHoverChange(open: boolean) {
    // Do not show tooltip when the popover is open
    if (!this.isAccountClicked) this.isAccountHovered = open;
  }

  onEnsembleChange(ensembleId: number | string) {
    if (_isString(ensembleId)) return; // string is only used when there is no ensemble

    const {history, session} = this.props.store!;

    const activeEnsemble = session.activeEnsembles?.find((activeEnsemble) => activeEnsemble.id === ensembleId);
    if (activeEnsemble) {
      session.setSelectedEnsembleById(ensembleId);
      // Reload because:
      // - we might be on a page not visible for the current role (in which case the page will redirect to home)
      // - the content of the page probably needs to be reloaded (e.g. pieces)
      window.location.reload();
      return;
    }

    const inactiveEnsemble = session.inactiveEnsembles?.find((inactiveEnsemble) => inactiveEnsemble.id === ensembleId);
    if (!inactiveEnsemble) throw new Error("Ensemble not found");

    // owner is the current user
    if (inactiveEnsemble.owner_id === session.user!.id) {
      Modal.confirm({
        title: "Your subscription is not active",
        content: (
          <span>
            You are the owner of the ensemble "{inactiveEnsemble.name}". Please activate your subscription again so the
            ensemble is accessible by you and its members.
          </span>
        ),
        cancelText: "Close",
        okText: "Subscribe",
        onOk: () => history.history.push(paths.subscription()),
      });
      // owner is somebody else
    } else {
      if (inactiveEnsemble.owner.active_subscriptions.length < 1) {
        Modal.warning({
          title: "Ensemble is not active",
          content: (
            <span>
              The ensemble "{inactiveEnsemble.name}" has no active subscription. Please contact the ensemble owner to
              activate the subscription again.
            </span>
          ),
        });
        // owner has an active subscription, the problem is with the current user
      } else {
        const ownerSubscription = inactiveEnsemble.owner.active_subscriptions[0];
        if (session.hasPlan("Basic", ownerSubscription.plan_id))
          Modal.warning({
            title: "Ensemble is not active",
            content: (
              <span>
                The ensemble "{inactiveEnsemble.name}" belongs to a user subscribed to a Basic plan, which does not
                allow to have ensembles with members. Please contact the ensemble owner to change the subscription plan.
              </span>
            ),
          });
        else if (session.hasPlan("Pro", ownerSubscription.plan_id) && !session.activeSubscription)
          Modal.confirm({
            title: "Your subscription is not active",
            content: (
              <span>
                The ensemble "{inactiveEnsemble.name}" belongs to a user subscribed to a Pro plan which requires you to
                have an active subscription to access it.
              </span>
            ),
            cancelText: "Close",
            okText: "Subscribe",
            onOk: () => history.history.push(paths.subscription()),
          });
        else
          Modal.warning({
            title: "Ensemble not accessible",
            content: (
              <span>
                Cannot determine the reason why the ensemble "{inactiveEnsemble.name}" is not accessible. Please contact
                us too find a solution.
              </span>
            ),
          });
      }
    }
  }

  @action
  openAddPieceModal() {
    this.addPieceModalVisible = true;
  }

  render() {
    const {children, store} = this.props;
    const {session} = store!;
    const {activeEnsembles, ensembles, inactiveEnsembles, selectedEnsemble} = session;
    return (
      <div className="Header">
        {children ?? (
          <Link className="logo" to={paths.home()}>
            <img src={logo} alt="Cantāmus" />
          </Link>
        )}
        <div className="vcenter">
          {!_isEmpty(ensembles) && (
            <Select<number | string>
              className="ensemble-select"
              bordered={false}
              value={selectedEnsemble?.id ?? "No ensemble"}
              onChange={this.onEnsembleChange}
              dropdownMatchSelectWidth={false}
            >
              {activeEnsembles?.map((ensemble) => (
                <Option key={ensemble.id} value={ensemble.id}>
                  {`${ensemble.name} (${
                    ensemble.owner_id === session.user!.id ? "owner" : ensemble.user_ensembles?.[0]?.role
                  })`}
                </Option>
              ))}
              {!_isEmpty(inactiveEnsembles) && (
                <OptGroup label="Inactive ensembles">
                  {inactiveEnsembles?.map((ensemble) => (
                    <Option key={ensemble.id} value={ensemble.id}>
                      {`${ensemble.name} (${
                        ensemble.owner_id === session.user!.id ? "owner" : ensemble.user_ensembles?.[0]?.role
                      })`}
                    </Option>
                  ))}
                </OptGroup>
              )}
            </Select>
          )}
          <Tooltip title="Help" placement="bottom">
            <a href={paths.help()} style={{marginRight: "0.5em"}} target="_blank" rel="help noreferrer">
              <Button shape="circle" icon={<QuestionOutlined />} size="small" />
            </a>
          </Tooltip>
          {session.isSignedIn && (
            <>
              {selectedEnsemble && (
                <>
                  {session.isEnsembleConductor && (session.hasPlanEnsemble || session.hasPlanPro) && (
                    <Tooltip title="Ensemble Rehearsals" placement="bottom">
                      {/* Better solution: https://stackoverflow.com/a/44878089/1513130 */}
                      <Link to={paths.ensembleRehearsals()} style={{marginRight: "0.5em"}}>
                        <Button
                          shape="circle"
                          icon={<TeamOutlined />}
                          size="small"
                          {...this.buttonActiveProps(paths.ensembleRehearsals())}
                        />
                      </Link>
                    </Tooltip>
                  )}
                  <Tooltip title="Rehearsals" placement="bottom">
                    {/* Better solution: https://stackoverflow.com/a/44878089/1513130 */}
                    <Link to={paths.rehearsals()} style={{marginRight: "0.5em"}}>
                      <Button
                        shape="circle"
                        icon={<AudioOutlined />}
                        size="small"
                        {...this.buttonActiveProps(paths.rehearsals())}
                      />
                    </Link>
                  </Tooltip>
                  <Tooltip title="My Repertoire" placement="bottom">
                    {/* Better solution: https://stackoverflow.com/a/44878089/1513130 */}
                    <Link to={paths.pieces()} style={{marginRight: "0.5em"}}>
                      <Button
                        className="my-repertoire-btn"
                        shape="circle"
                        icon={<ReadOutlined />}
                        size="small"
                        {...this.buttonActiveProps(paths.pieces())}
                      />
                    </Link>
                  </Tooltip>
                  {session.canAddPiece ? (
                    <Tooltip title="Add Piece" placement="bottom">
                      <Link to={paths.pieceNew()} style={{marginRight: "0.5em"}}>
                        {/* Better solution: https://stackoverflow.com/a/44878089/1513130 */}
                        <Button
                          className="my-repertoire-btn"
                          shape="circle"
                          icon={<PlusOutlined />}
                          size="small"
                          {...this.buttonActiveProps(paths.pieceNew())}
                        />
                      </Link>
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip title="Add Piece" placement="bottom">
                        <Button
                          className="my-repertoire-btn"
                          shape="circle"
                          icon={<PlusOutlined />}
                          size="small"
                          onClick={this.openAddPieceModal}
                          style={{marginRight: "0.5em"}}
                        />
                      </Tooltip>
                      <AddPieceUpgradeModal close={this.closeAddPieceModal} visible={this.addPieceModalVisible} />
                    </>
                  )}
                </>
              )}
              <Tooltip
                title="Account"
                placement="bottom"
                open={this.isAccountHovered}
                onOpenChange={this.handleAccountHoverChange}
              >
                <Popover
                  content={<AccountPopover />}
                  placement="bottomRight"
                  arrowPointAtCenter
                  trigger="click"
                  open={this.isAccountClicked}
                  onOpenChange={this.handleAccountClickChange}
                >
                  <Button
                    className="my-profile-btn"
                    shape="circle"
                    icon={<UserOutlined />}
                    size="small"
                    {...this.buttonActiveProps([paths.selectedEnsemble(), paths.ensembles(), paths.subscription()])}
                  />
                </Popover>
              </Tooltip>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default withRouter(Header);
